import React, { useState } from "react";
import './portfolio.css'
import ExplainerModal from "../explainer/ExplainerModal";
import { FiHelpCircle } from "react-icons/fi";
import useReveal from "../../hooks/useReveal";
import GCPLogo from "../../assets/gcp_logo.png";
import BigQueryLogo from "../../assets/bigquery_logo.png";
import AILogo from "../../assets/ai_logo.png";
import TensorFlowLogo from "../../assets/tf_logo.jpg";

const data = [
    {
        id: 0,
        image: AILogo,
        title: "AI Brand-Analytics Platform — BrightEdge",
        description: "Architected the data platform measuring brand visibility across generative-AI search engines. Optimized ClickHouse for terabyte-scale streaming ingestion and built a Trino + Iceberg lakehouse with sub-10-min prompt-to-data SLA.",
        technologies: "Python, FastAPI, ClickHouse, Trino, Apache Iceberg, Argo Workflows, GCP, Kubernetes",
        explainer: {
            summary: "Measures brand visibility across generative-AI engines: answers are ingested from object storage, processed through a Trino + Iceberg lakehouse into ClickHouse, and served via low-latency APIs.",
            architecture: {
                zone: "GCP · Kubernetes · Argo Workflows",
                columns: [
                    { title: "AI Engines", nodes: [
                        { label: "ChatGPT", icon: "openai" },
                        { label: "Perplexity", icon: "ai" },
                        { label: "Google AIO", icon: "gcp" },
                    ] },
                    { title: "Object Store", nodes: [
                        { label: "GCS / S3", icon: "s3", sub: "Parquet" },
                    ] },
                    { title: "Lakehouse", nodes: [
                        { label: "Trino", icon: "trino" },
                        { label: "Iceberg", icon: "iceberg" },
                    ] },
                    { title: "Analytics", nodes: [
                        { label: "ClickHouse", icon: "clickhouse", sub: "TB-scale" },
                    ] },
                    { title: "Serving", nodes: [
                        { label: "FastAPI", icon: "fastapi", sub: "<3s cached" },
                        { label: "Dashboard", icon: "dashboard" },
                    ] },
                ],
            },
        },
    },
    {
        id: 1,
        image: GCPLogo,
        title: "Scalable GCP Infrastructure for Web Crawling",
        description: "Led transition to scalable GCP infra, improving efficiency >50%. Automated Kubernetes/Dataproc deployments with Terraform & Ansible, crawling millions of pages daily with failover and observability.",
        technologies: "GCP, Kubernetes, Terraform, Ansible, Dataproc, Grafana",
        explainer: {
            summary: "Cloud-native crawl fleet on GCP: containerized crawlers on Kubernetes/Dataproc, provisioned by Terraform & Ansible, autoscaling with failover and Grafana observability.",
            architecture: {
                zone: "GCP",
                columns: [
                    { title: "Provision", nodes: [
                        { label: "Terraform", icon: "terraform" },
                        { label: "Ansible", icon: "ansible" },
                    ] },
                    { title: "Compute", nodes: [
                        { label: "Kubernetes", icon: "k8s" },
                        { label: "Dataproc", icon: "spark" },
                    ] },
                    { title: "Crawl", nodes: [
                        { label: "Crawlers", icon: "python", sub: "millions/day" },
                    ] },
                    { title: "Store / Observe", nodes: [
                        { label: "GCS", icon: "gcp" },
                        { label: "Grafana", icon: "grafana" },
                    ] },
                ],
            },
        },
    },
    {
        id: 2,
        image: BigQueryLogo,
        title: "High-Volume Crawl Data Processing & Analytics",
        description: "Engineered pipelines processing millions of pages per customer monthly. Multi-stage architecture (Parquet → GCS → BigQuery → ClickHouse) with ClickHouse tuning via S3 functions, caching and Bloom filters.",
        technologies: "GCS, BigQuery, ClickHouse, Parquet, PyArrow, Python, Kubernetes",
        explainer: {
            summary: "Multi-stage analytics pipeline turning raw crawled pages into fast, queryable data: Parquet on GCS → BigQuery batch → ClickHouse for sub-second reporting.",
            architecture: {
                zone: "GCP · Kubernetes",
                columns: [
                    { title: "Raw", nodes: [
                        { label: "Crawled Pages", icon: "file" },
                    ] },
                    { title: "Format", nodes: [
                        { label: "Parquet", icon: "parquet" },
                        { label: "GCS", icon: "gcp" },
                    ] },
                    { title: "Batch", nodes: [
                        { label: "BigQuery", icon: "bigquery" },
                    ] },
                    { title: "Analytics", nodes: [
                        { label: "ClickHouse", icon: "clickhouse", sub: "sub-second" },
                    ] },
                ],
            },
        },
    },
    {
        id: 3,
        image: GCPLogo,
        title: "Monolith → Microservices Migration — Greendeck",
        description: "Migrated a retail-intelligence monolith to 3–5 microservices with event-driven Redis/Kafka pipelines crawling millions of SKUs daily, and deployed scalable Elasticsearch search on GCP.",
        technologies: "Python, Redis, Kafka, Elasticsearch, MongoDB, GCP",
        explainer: {
            summary: "Decomposed a retail-intelligence monolith into event-driven microservices communicating through Redis/Kafka queues, with Elasticsearch-powered search on GCP.",
            architecture: {
                zone: "GCP",
                columns: [
                    { title: "Legacy", nodes: [
                        { label: "Monolith", icon: "db" },
                    ] },
                    { title: "Event Bus", nodes: [
                        { label: "Kafka", icon: "kafka" },
                        { label: "Redis", icon: "redis" },
                    ] },
                    { title: "Microservices", nodes: [
                        { label: "Search", icon: "python" },
                        { label: "Ingestion", icon: "python" },
                        { label: "Matching", icon: "python" },
                    ] },
                    { title: "Index", nodes: [
                        { label: "Elasticsearch", icon: "elasticsearch" },
                    ] },
                ],
            },
        },
    },
    {
        id: 4,
        image: TensorFlowLogo,
        title: "Multimodal Product-Matching (Deep Learning)",
        description: "Built an image+text product-matching model reaching 97% top-10 / 92% top-5 accuracy, benchmarking Corrnet, VSE++ and DeepRank with BERT, SpaCy, Flair and FastText embeddings.",
        technologies: "TensorFlow, Python, BERT, SpaCy, FastText, GCP",
        explainer: {
            summary: "A multimodal model that matches product listings by both image and text — benchmarked across architectures (Corrnet, VSE++, DeepRank) and embeddings — reaching 97% top-10 accuracy.",
            architecture: {
                zone: "TensorFlow · GCP",
                columns: [
                    { title: "Inputs", nodes: [
                        { label: "Image", icon: "ai" },
                        { label: "Text", icon: "file" },
                    ] },
                    { title: "Embeddings", nodes: [
                        { label: "BERT / FastText", icon: "tensorflow" },
                    ] },
                    { title: "Model", nodes: [
                        { label: "Multimodal Net", icon: "tensorflow", sub: "Corrnet/VSE++" },
                    ] },
                    { title: "Output", nodes: [
                        { label: "Match", icon: "dashboard", sub: "97% top-10" },
                    ] },
                ],
            },
        },
    },
];

const Portfolio = () => {
    const ref = useReveal();
    const [active, setActive] = useState(null);
    return (
        <section id="portfolio">
            <h5>$ git log --work</h5>
            <h2>Selected Work</h2>
            <div className="container portfolio__container reveal" ref={ref}>
                {data.map(({ id, image, title, description, technologies, explainer }) => (
                    <article key={id} className="portfolio__item term-window">
                        <div className="term-titlebar">
                            <span className="term-dot red"></span>
                            <span className="term-dot yellow"></span>
                            <span className="term-dot green"></span>
                        </div>
                        <div className="portfolio__item-body">
                            <div className="portfolio__item-image">
                                <img src={image} alt={title}/>
                            </div>
                            <h3>{title}</h3>
                            <p className="portfolio__item-description">{description}</p>
                            <small className="portfolio__item-technologies mono txt-muted">{technologies}</small>
                            <button
                                className="know-more mono"
                                onClick={() => setActive({ title, ...explainer })}
                            >
                                <FiHelpCircle/> explain --simple
                            </button>
                        </div>
                    </article>
                ))}
            </div>
            <ExplainerModal explainer={active} onClose={() => setActive(null)}/>
        </section>
    )
}

export default Portfolio
